/**
 * Where the host remembers which bookings tab the tenant was on.
 *
 * ⛔ ITS OWN ENTRY, NOT A FIELD ON `resource-booking.selected-booking`. That one is a contract with
 * tenant-portal, which WRITES it too -- `rememberSelectedBooking` stringifies a whole fresh object
 * -- so a `tab` field added on this side would survive exactly until the next portal write and
 * then vanish, intermittently and silently. A sibling key nobody else writes cannot be clobbered.
 * The activity-center miniapp splits the same two concerns the same way: `activity-center.selection`
 * for the open record, `activity-center.feed-view` for which view it was open in.
 */
export const BOOKINGS_TAB_STORAGE_KEY = 'resource-booking.bookings-tab';

/**
 * The query param a link uses to name the tab it wants: `?tab=past`.
 *
 * ⭐ OUTRANKS THE STORED ENTRY, because it is the only one of the two that KNOWS. Which tab a
 * booking sits on is decided by the service -- the list url fixes `is_upcoming`, and the
 * single-record read carries no such field -- so this app cannot work out from a booking alone
 * which list holds it. `hasBookingEnded` is not that test either: helix keeps an ended booking on
 * Upcoming for another half hour. Whoever minted the link had the answer; this is them passing it.
 *
 * Read once per mount and never written. A tab in the url would put tab switching into the history
 * stack, which is why the tenant's own presses stay out of it -- an incoming link is not a press.
 */
export const BOOKINGS_TAB_PARAM = 'tab';

/**
 * The window the entry stays good for, matching the selection's.
 *
 * A reload window, not a session memory. Nothing here can tell "reloaded the page" from "came back
 * on Thursday", and the TTL is what keeps the second one out. Expiring is also the benign direction
 * for a tab: it falls back to `DEFAULT_BOOKINGS_TAB`, which is where most arrivals belong anyway.
 */
export const BOOKINGS_TAB_TTL_MS = 5 * 60 * 1000;

export interface StoredBookingsTab {
  /** One of `BOOKINGS_TAB_IDS`; anything else is discarded on the way back in. */
  tab: string;
  savedAt: number;
}

/**
 * How long the page holds its automatic first-booking pick while the host's answer is in flight.
 *
 * ⚠️ A FAILURE ARM, NOT A DELAY. The answer is a postMessage round trip and normally beats the
 * list's HTTP fetch by a wide margin, so this timer almost never fires. It exists for the host that
 * is asked and never replies: without it the pane layout would wait on an answer that is not coming
 * and open nothing at all, which is worse than opening the wrong list's first booking. The same arm
 * `useRestoreSelectedBooking` gives its own wait through `isNotFound`.
 */
export const BOOKINGS_TAB_HOST_ANSWER_TIMEOUT_MS = 1500;
